import type { ReactNode } from 'react';
import { Alert, QueryErrorAlert } from '@shared/components';
import type { ThirdPartyInvoicingDealer } from '../../hooks/useThirdPartyInvoicingDealer';
import type { ThirdPartyInvoiceableDealer } from '../../types/thirdPartyAgreement.types';
import {
    THIRD_PARTY_DEALERS_FAILED,
    THIRD_PARTY_DEALERS_LOADING,
    THIRD_PARTY_INVOICING_NOT_FOUND,
    THIRD_PARTY_NO_DEALERS,
} from '../../utils/thirdPartyInvoicingCopy';
import { INVOICING_DEALER_GATED_NOTE, InvoicingDealerField } from '../InvoicingDealerField';

interface Props {
    dealer: ThirdPartyInvoicingDealer;
}

const FIELD_ID = 'third-party-invoicing-dealer';
const CAPTION_ID = `${FIELD_ID}-caption`;
const NO_OPTIONS: ThirdPartyInvoiceableDealer[] = [];

function CaptionedMessage({ children }: { children: ReactNode }) {
    return (
        <div>
            <p id={CAPTION_ID} className="mb-1.5 text-sm font-medium text-mp-foreground">
                Invoicing dealer
            </p>
            {children}
        </div>
    );
}

function DealerFailure({ message, dealer }: { message: string; dealer: ThirdPartyInvoicingDealer }) {
    return (
        <CaptionedMessage>
            <div role="group" aria-labelledby={CAPTION_ID}>
                <QueryErrorAlert message={message} onRetry={dealer.retry} isRetrying={dealer.retrying} />
            </div>
        </CaptionedMessage>
    );
}

function NoDealers() {
    return (
        <CaptionedMessage>
            <Alert variant="info" role="status" ariaLabelledBy={CAPTION_ID}>
                {THIRD_PARTY_NO_DEALERS}
            </Alert>
        </CaptionedMessage>
    );
}

export function ThirdPartyInvoicingDealerField({ dealer }: Props) {
    switch (dealer.field) {
        case 'not-found':
            return <DealerFailure message={THIRD_PARTY_INVOICING_NOT_FOUND} dealer={dealer} />;
        case 'failed':
            return <DealerFailure message={THIRD_PARTY_DEALERS_FAILED} dealer={dealer} />;
        case 'empty':
            return <NoDealers />;
        case 'gated':
            return (
                <InvoicingDealerField<ThirdPartyInvoiceableDealer>
                    id={FIELD_ID}
                    options={NO_OPTIONS}
                    value={null}
                    onChange={dealer.select}
                    getOptionValue={(option) => option.encryptedId}
                    getOptionLabel={(option) => option.publicName}
                    disabled
                    emptyLabel={INVOICING_DEALER_GATED_NOTE}
                    note={INVOICING_DEALER_GATED_NOTE}
                />
            );
        default:
            return (
                <InvoicingDealerField<ThirdPartyInvoiceableDealer>
                    id={FIELD_ID}
                    options={dealer.dealers}
                    value={dealer.selected}
                    onChange={dealer.select}
                    getOptionValue={(option) => option.encryptedId}
                    getOptionLabel={(option) => option.publicName}
                    loading={dealer.field === 'loading'}
                    disabled={dealer.field === 'loading'}
                    emptyLabel={THIRD_PARTY_NO_DEALERS}
                    note={dealer.field === 'loading' ? THIRD_PARTY_DEALERS_LOADING : null}
                />
            );
    }
}
